import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ExerciseMergeScreen - Manual Canonical Merge for exactly 2 exercises
 * 
 * Features:
 * - Side-by-side comparison of selected exercises
 * - Impact analysis (workouts, clients, feedback, PRs)
 * - Field-by-field selection for merged result
 * - Hard delete option for zero-usage exercises
 * - Transactional merge execution
 * 
 * Note: Uses React Portal to render at document root level to avoid
 * z-index and overflow issues from parent containers.
 */
const ExerciseMergeScreen = ({ 
  selectedExercises, // Array of 2 exercise IDs
  exercises,         // Full exercise data array
  onClose, 
  onMergeComplete,
  language, 
  t 
}) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [swapped, setSwapped] = useState(false); // Allow user to swap canonical/duplicate
  const [confirmStep, setConfirmStep] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  // Fetch merge preview on mount
  useEffect(() => {
    const fetchPreview = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.post(
          `${API}/exercises/merge/preview`,
          { exercise_ids: selectedExercises }
        );
        
        if (!response.data.valid) {
          setError(response.data.error);
        } else {
          setPreview(response.data);
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to fetch merge preview');
      } finally {
        setLoading(false);
      }
    };

    if (selectedExercises.length === 2) {
      fetchPreview();
    }
  }, [selectedExercises]);

  // Get canonical and duplicate based on swap state
  const canonical = preview && (swapped ? preview.duplicate : preview.canonical);
  const duplicate = preview && (swapped ? preview.canonical : preview.duplicate);

  // Execute merge
  const handleExecuteMerge = async () => {
    if (!canonical || !duplicate) return;
    
    try {
      setExecuting(true);
      
      const response = await axios.post(
        `${API}/exercises/merge/execute`,
        {
          canonical_id: canonical.id,
          duplicate_id: duplicate.id
        }
      );
      
      if (response.data.success) {
        onMergeComplete(response.data);
      } else {
        setError('Merge failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Merge execution failed');
    } finally {
      setExecuting(false);
    }
  };

  // Execute hard delete
  const handleHardDelete = async () => {
    if (!duplicate) return;
    
    try {
      setExecuting(true);
      
      const response = await axios.delete(
        `${API}/exercises/merge/hard-delete/${duplicate.id}`
      );
      
      if (response.data.success) {
        onMergeComplete({
          ...response.data,
          action: 'hard_delete'
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    } finally {
      setExecuting(false);
    }
  };

  // Helper function to render modal content in a portal
  const renderInPortal = (content) => {
    return ReactDOM.createPortal(content, document.body);
  };

  // Render loading state
  if (loading) {
    return renderInPortal(
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]" data-testid="merge-screen-loading">
        <div className="glass-card p-8 max-w-md text-center">
          <div className="animate-spin text-4xl mb-4">🔄</div>
          <p className="text-blue-200">Analyzing exercises for merge...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return renderInPortal(
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999]" data-testid="merge-screen-error">
        <div className="glass-card p-8 max-w-md">
          <div className="text-red-400 text-4xl mb-4 text-center">⚠️</div>
          <p className="text-red-200 text-center mb-4">{error}</p>
          <div className="flex justify-center gap-3">
            <button 
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!preview) return null;

  // Confirmation step
  if (confirmStep) {
    const expectedText = duplicate.name.toLowerCase().substring(0, 10);
    const isValid = confirmText.toLowerCase() === expectedText;

    return renderInPortal(
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4" data-testid="merge-confirm-modal">
        <div className="glass-card p-6 max-w-lg w-full max-h-[90dvh] overflow-y-auto">
          <h2 className="text-xl font-bold text-white mb-4">🔐 Confirm Merge</h2>
          
          <div className="bg-red-500/20 border border-red-400/40 rounded-lg p-4 mb-4">
            <p className="text-red-200 font-medium mb-2">⚠️ This action cannot be undone!</p>
            <p className="text-red-100 text-sm">
              "{duplicate.name}" will be permanently marked as merged. 
              All {preview.impact?.workouts_affected || 0} workout references will be migrated to "{canonical.name}".
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-blue-200 text-sm mb-2">
              Type <span className="font-mono bg-gray-700 px-2 py-1 rounded">{expectedText}</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="input-field w-full"
              placeholder="Type to confirm..."
              autoFocus
              data-testid="merge-confirm-input"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setConfirmStep(false);
                setConfirmText('');
              }}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
              disabled={executing}
            >
              Cancel
            </button>
            <button
              onClick={handleExecuteMerge}
              disabled={!isValid || executing}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                isValid && !executing
                  ? 'bg-red-500 hover:bg-red-600 text-white'
                  : 'bg-gray-500 text-gray-300 cursor-not-allowed'
              }`}
              data-testid="merge-execute-btn"
            >
              {executing ? '⏳ Merging...' : '🔗 Execute Merge'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return renderInPortal(
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4 overflow-y-auto" data-testid="merge-screen">
      <div className="glass-card p-6 max-w-5xl w-full my-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">🔗 Merge Exercises</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
            data-testid="merge-close-btn"
          >
            ✕
          </button>
        </div>

        {/* Impact Summary */}
        <div className="bg-blue-900/30 border border-blue-400/40 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-200 mb-3">📊 Merge Impact</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{preview.impact?.workouts_affected || 0}</div>
              <div className="text-blue-300 text-xs">Workouts</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{preview.impact?.clients_affected || 0}</div>
              <div className="text-blue-300 text-xs">Clients</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{preview.impact?.feedback_entries_affected || 0}</div>
              <div className="text-blue-300 text-xs">Feedback</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{preview.impact?.prs_affected || 0}</div>
              <div className="text-blue-300 text-xs">PR Records</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{preview.impact?.translations_affected || 0}</div>
              <div className="text-blue-300 text-xs">Translations</div>
            </div>
          </div>
        </div>

        {/* Side by Side Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Canonical (Keep) */}
          <div className="bg-green-500/10 border-2 border-green-400/60 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-green-300">✅ KEEP (Canonical)</h3>
              <button
                onClick={() => setSwapped(!swapped)}
                className="px-3 py-1 bg-blue-500/30 hover:bg-blue-500/50 text-blue-200 rounded text-sm"
                title="Swap canonical and duplicate"
                data-testid="swap-exercises-btn"
              >
                🔄 Swap
              </button>
            </div>
            
            {canonical?.image_url && (
              <img 
                src={canonical.image_url} 
                alt={canonical.name}
                className="w-full h-32 object-cover rounded-lg mb-3"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            
            <h4 className="text-white font-semibold text-lg mb-2">{canonical?.name}</h4>
            {canonical?.name_es && (
              <p className="text-green-200 text-sm mb-2">🇪🇸 {canonical.name_es}</p>
            )}
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Category:</span>
                <span className="text-white">{canonical?.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Difficulty:</span>
                <span className="text-white">{canonical?.difficulty || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Equipment:</span>
                <span className="text-white">{canonical?.equipment_ids?.join(', ') || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Usage Count:</span>
                <span className="text-green-300 font-medium">{canonical?.usage_stats?.usage_count || 0}</span>
              </div>
            </div>
          </div>

          {/* Duplicate (Merge Into) */}
          <div className="bg-red-500/10 border-2 border-red-400/60 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-red-300">❌ MERGE (Duplicate)</h3>
              {preview.allow_hard_delete && duplicate?.usage_stats?.usage_count === 0 && (
                <span className="px-2 py-1 bg-yellow-500/30 text-yellow-200 rounded text-xs">
                  Zero Usage
                </span>
              )}
            </div>
            
            {duplicate?.image_url && (
              <img 
                src={duplicate.image_url} 
                alt={duplicate.name}
                className="w-full h-32 object-cover rounded-lg mb-3 opacity-70"
                onError={(e) => e.target.style.display = 'none'}
              />
            )}
            
            <h4 className="text-white font-semibold text-lg mb-2 line-through opacity-70">{duplicate?.name}</h4>
            {duplicate?.name_es && (
              <p className="text-red-200 text-sm mb-2 line-through opacity-70">🇪🇸 {duplicate.name_es}</p>
            )}
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Category:</span>
                <span className="text-white opacity-70">{duplicate?.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Difficulty:</span>
                <span className="text-white opacity-70">{duplicate?.difficulty || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Equipment:</span>
                <span className="text-white opacity-70">{duplicate?.equipment_ids?.join(', ') || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Usage Count:</span>
                <span className="text-red-300 font-medium">{duplicate?.usage_stats?.usage_count || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Post-Merge Info */}
        <div className="bg-purple-900/20 border border-purple-400/30 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-purple-200 mb-2">📋 After Merge</h3>
          <ul className="text-purple-100 text-sm space-y-1">
            <li>• All workout references will point to "{canonical?.name}"</li>
            <li>• Exercise feedback will be migrated</li>
            <li>• PR records will remain intact under the canonical exercise</li>
            <li>• "{duplicate?.name}" will be marked as "merged" (hidden from library)</li>
            <li>• This operation is <strong>transactional</strong> - it will fully succeed or fully rollback</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition"
            disabled={executing}
          >
            Cancel
          </button>
          
          {/* Hard Delete option - only if zero usage */}
          {preview.allow_hard_delete && duplicate?.usage_stats?.usage_count === 0 && (
            <button
              onClick={handleHardDelete}
              disabled={executing}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition font-medium disabled:opacity-50"
              title="Permanently delete the duplicate (it has zero usage)"
              data-testid="hard-delete-btn"
            >
              {executing ? '⏳...' : '🗑️ Hard Delete'}
            </button>
          )}
          
          <button
            onClick={() => setConfirmStep(true)}
            disabled={executing}
            className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition font-medium disabled:opacity-50"
            data-testid="merge-btn"
          >
            {executing ? '⏳ Processing...' : '🔗 Merge Exercises'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExerciseMergeScreen;
